import React, { FC, useMemo, useState } from 'react';
import { gql, useFragment } from '@apollo/client';
import classNames from 'classnames';

import { analyticService } from 'global/services';
import { useBasketQueryLocate } from 'app/hooks/useBasketQueryLocate';

import css from './TabBar.module.scss';

export const TabBar: FC<any> = ({
    selectedDishId,
    ingredientsList,
    onNextIdSelected,
}: any) => {
    const dishesWithCustomizationInfo = useMemo(
        () => {
            const isNMCustomization = ingredientsList.some((item: any) => item.secondDishPartNM === 5);
            const greenVersion = ingredientsList.find((item: any) => {
                if (isNMCustomization) {
                    return item.secondDishPartID !== null && item.id.toString() === item.secondDishPartID.toString();
                }
                return item.secondDishPart === 5;
            }) || ingredientsList.find((item: any) => {
                if (isNMCustomization) {
                    return item.secondDishPartID !== null;
                }
                return item.secondDishPart === 5;
            });
            const regularVersion = ingredientsList.find((item: any) => (
                isNMCustomization
                    ? item.id !== greenVersion?.id
                    : item.secondDishPart !== 5
            ),
            );
            return { greenVersion, regularVersion };
        },
        [ingredientsList],
    );

    const [basketData] = useBasketQueryLocate();

    const [activeOptionId, setActiveOptionId] = useState<string>(selectedDishId);

    const { data: { title: greenDishTitle, price: greenDishPrice } } = useFragment(
        {
            fragment: gql`
                fragment GREEN_DISH_FRAGMENT on menuDish {
                    title
                    price
                }
            `,
            from: {
                __ref: `menuDish:${dishesWithCustomizationInfo.greenVersion?.id}`,
            },
        },
    );

    const { data: { title: regularDishTitle, price: regularDishPrice } } = useFragment(
        {
            fragment: gql`
                fragment REGULAR_DISH_FRAGMENT on menuDish {
                    title
                    price
                }
            `,
            from: {
                __ref: `menuDish:${dishesWithCustomizationInfo.regularVersion?.id}`,
            },
        },
    );

    const addedDishes = useMemo(
        () => {
            const flatSection = basketData?.cart?.sections
                ?.reduce((acc: any[], section: any) => [...acc, ...section.items], []);
            return flatSection;
        },
        [basketData],
    );

    const regularVersionFromCart = useMemo(
        () => addedDishes?.find((item: any) => item.dish_id === dishesWithCustomizationInfo.regularVersion?.id),
        [addedDishes, dishesWithCustomizationInfo],
    );

    const greenVersionFromCart = useMemo(
        () => addedDishes?.find((item: any) => item.dish_id === dishesWithCustomizationInfo.greenVersion?.id),
        [addedDishes, dishesWithCustomizationInfo],
    );

    const handleSendAnalytics = (eventName: string) => {
        const actionOptionTitles: any = {
            [dishesWithCustomizationInfo.regularVersion?.id]: regularDishTitle,
            [dishesWithCustomizationInfo.greenVersion?.id]: greenDishTitle,
        };
        const activeOptionPrices: any = {
            [dishesWithCustomizationInfo.regularVersion?.id]: regularDishPrice,
            [dishesWithCustomizationInfo.greenVersion?.id]: greenDishPrice,
        };
        const dishesNodes = Array.from(document.querySelectorAll('[data-dish-id]'))
            .map((e) => e.getAttribute('data-dish-id'));
        analyticService
            .push({
                eventName,
                params: {
                    dish_id: activeOptionId,
                    dish_name: actionOptionTitles[activeOptionId as keyof typeof actionOptionTitles],
                    dish_index: dishesNodes.indexOf(activeOptionId) + 1,
                    price: activeOptionPrices[activeOptionId as keyof typeof activeOptionPrices],
                    source_name: 'new_menu',
                },
            });
    };

    const handleOptionClick = (nextId: string) => {
        setActiveOptionId(nextId);
        onNextIdSelected(nextId);
    };

    const activeBarClasses = classNames({
        'shadow-l': true,
        [css.tabBarActiveOptionBar]: true,
        [css.translated]: activeOptionId === dishesWithCustomizationInfo.greenVersion?.id,
    });

    return (
        <div className={css.tabBarRoot}>
            <div
                className={css.tabBarOption}
                onClick={() => {
                    handleOptionClick(dishesWithCustomizationInfo.regularVersion?.id);
                    handleSendAnalytics('Click_Unselect_HLS');
                }}
                role="none"
            >
                <span
                    className={`body-s-medium ${css.tabBarOptionText}
                    ${activeOptionId === dishesWithCustomizationInfo.regularVersion?.id && css.active}`}
                >
                    обычная версия
                    {activeOptionId !== dishesWithCustomizationInfo.regularVersion?.id && regularVersionFromCart && (
                        <span className={`other-shield-caps-medium ${css.tabBarPortionsBadge}`}>
                            {regularVersionFromCart.portions}
                        </span>
                    )}
                </span>
            </div>
            <div
                className={css.tabBarOption}
                onClick={() => {
                    handleOptionClick(dishesWithCustomizationInfo.greenVersion?.id);
                    handleSendAnalytics('Click_Select_HLS');
                }}
                role="none"
            >
                <span
                    className={`body-s-medium ${css.tabBarOptionText}
                    ${activeOptionId === dishesWithCustomizationInfo.greenVersion?.id && css.active}`}
                >
                    green версия
                    {activeOptionId !== dishesWithCustomizationInfo.greenVersion?.id && greenVersionFromCart && (
                        <span className={`other-shield-caps-medium ${css.tabBarPortionsBadge}`}>
                            {greenVersionFromCart.portions}
                        </span>
                    )}
                </span>
            </div>
            <div className={activeBarClasses} />
        </div>
    );
};


// const { secondDishPart, replaceableIngredient } = useFragment({
//     fragment: gql`
//         fragment DISH_CUSTOM_INGREDIENTS_FRAGMENT on menuDish {
//             replaceableIngredient,
//             secondDishPart,
//             parentId,
//             category_id,
//         }`,
//     from: {
//         __ref: `menuDish:${selectedDishId}`,
//     },
// });
